<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content materials-manager" @click.stop>
      <div class="modal-header">
        <h2>
          <i class="fas fa-folder-open"></i>
          Manage Course Materials
        </h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="video-info">
          <h3>{{ video.title }}</h3>
          <p class="text-muted">Add downloadable materials like slides, PDFs, and documents</p>
        </div>

        <!-- File Upload Section -->
        <div class="upload-section">
          <div class="upload-area" :class="{ 'drag-over': isDragging }" @drop.prevent="handleDrop" @dragover.prevent="isDragging = true" @dragleave="isDragging = false">
            <input
              ref="fileInput"
              type="file"
              multiple
              accept=".pdf,.ppt,.pptx,.doc,.docx,.jpg,.jpeg,.png,.gif"
              @change="handleFileSelect"
              style="display: none"
            />
            <div class="upload-icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <h4>Drag & Drop Files Here</h4>
            <p>or</p>
            <button @click="$refs.fileInput.click()" class="btn btn-primary">
              <i class="fas fa-folder-open"></i>
              Browse Files
            </button>
            <p class="upload-hint">Supported: PDF, PPT, PPTX, DOC, DOCX, JPG, PNG (Max 50MB each)</p>
          </div>

          <!-- Upload Progress -->
          <div v-if="uploading" class="upload-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
            </div>
            <p>Uploading {{ uploadingFile }}... {{ uploadProgress }}%</p>
          </div>
        </div>

        <!-- Materials List -->
        <div v-if="materials.length > 0" class="materials-list">
          <h4>Course Materials ({{ materials.length }})</h4>
          <div class="materials-grid">
            <div
              v-for="(material, index) in materials"
              :key="material.id"
              class="material-card"
            >
              <div class="material-thumbnail">
                <img v-if="material.thumbnail_url" :src="material.thumbnail_url" />
                <div v-else class="thumbnail-placeholder">
                  <i :class="getFileIcon(material.type)"></i>
                </div>
              </div>
          <div class="material-info">
            <input
              v-model="material.title"
              type="text"
              class="material-title-input"
              placeholder="Material title..."
              @input="markDirty"
            />
            <textarea
              v-model="material.description"
              class="material-description-input"
              placeholder="Add a description (what will students learn from this?)..."
              rows="2"
              @input="markDirty"
            ></textarea>
            <div class="material-meta">
              <span class="file-type">{{ material.type.toUpperCase() }}</span>
              <span v-if="material.file_size" class="file-size">{{ material.file_size }}</span>
            </div>
          </div>
              <div class="material-actions">
                <button @click="moveMaterial(index, -1)" :disabled="index === 0" class="btn-icon" title="Move up">
                  <i class="fas fa-arrow-up"></i>
                </button>
                <button @click="moveMaterial(index, 1)" :disabled="index === materials.length - 1" class="btn-icon" title="Move down">
                  <i class="fas fa-arrow-down"></i>
                </button>
                <button @click="deleteMaterial(index)" class="btn-icon btn-danger" title="Delete">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-materials">
          <i class="fas fa-inbox"></i>
          <p>No course materials yet. Upload files to get started.</p>
        </div>
      </div>

      <div class="modal-footer">
        <p v-if="!isDirty" class="auto-save-note">
          <i class="fas fa-check-circle"></i>
          Materials are automatically saved when uploaded
        </p>
        <p v-else class="changes-note">
          <i class="fas fa-exclamation-circle"></i>
          You have unsaved changes
        </p>
        <div class="footer-buttons">
          <button v-if="isDirty" @click="saveMaterials" class="btn btn-success" :disabled="saving">
            <i class="fas fa-save"></i>
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
          <button @click="$emit('close')" class="btn" :class="isDirty ? 'btn-secondary' : 'btn-primary'">
            <i class="fas fa-check"></i>
            {{ isDirty ? 'Cancel' : 'Done' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'

export default {
  name: 'CourseMaterialsManager',
  props: {
    video: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const toast = useToast()
    const materials = ref([])
    const isDragging = ref(false)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const uploadingFile = ref('')
    const saving = ref(false)
    const fileInput = ref(null)
    const isDirty = ref(false)

    const markDirty = () => {
      console.log('Course materials modified - changes need to be saved')
      isDirty.value = true
    }

    onMounted(() => {
      // Load existing materials from video
      materials.value = props.video.course_materials ? [...props.video.course_materials] : []
    })

    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      uploadFiles(files)
    }

    const handleDrop = (event) => {
      isDragging.value = false
      const files = Array.from(event.dataTransfer.files)
      uploadFiles(files)
    }

    const uploadFiles = async (files) => {
      for (const file of files) {
        await uploadFile(file)
      }
    }

    const uploadFile = async (file) => {
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 50MB.`)
        return
      }

      uploading.value = true
      uploadingFile.value = file.name
      uploadProgress.value = 0

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('title', file.name.replace(/\.[^/.]+$/, ''))

        // Upload to S3 via backend API
        await lmsAPI.uploadCourseMaterial(props.video.id, formData, (progress) => {
          uploadProgress.value = progress
        })

        // Reload the video to get updated materials list
        const videoResponse = await lmsAPI.getVideo(props.video.id)
        materials.value = videoResponse.data.course_materials || []

        toast.success(`${file.name} uploaded successfully!`)
      } catch (error) {
        console.error('Upload error:', error)
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.error || error.message}`)
      } finally {
        uploading.value = false
        uploadingFile.value = ''
        uploadProgress.value = 0
      }
    }

    const getFileIcon = (type) => {
      const icons = {
        'pdf': 'fas fa-file-pdf',
        'slide': 'fas fa-file-powerpoint',
        'document': 'fas fa-file-word',
        'image': 'fas fa-file-image'
      }
      return icons[type] || 'fas fa-file'
    }

    const moveMaterial = (index, direction) => {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < materials.value.length) {
        const temp = materials.value[index]
        materials.value[index] = materials.value[newIndex]
        materials.value[newIndex] = temp
        markDirty()
      }
    }

    const deleteMaterial = (index) => {
      if (confirm('Are you sure you want to delete this material?')) {
        console.log('Deleting material at index:', index, 'Material:', materials.value[index])
        materials.value.splice(index, 1)
        console.log('Materials after delete:', materials.value.length, 'items remaining')
        markDirty()
      }
    }

    const saveMaterials = async () => {
      // Save material changes via custom endpoint
      saving.value = true
      try {
        console.log('Saving course materials for video:', props.video.id)
        console.log('Materials to save:', materials.value)
        
        // Use PATCH to update only course_materials field
        // Don't send the entire video object, just the fields we want to update
        const api = (await import('@/services/api.js')).default
        const response = await api.patch(`/lms/videos/${props.video.id}/`, {
          course_materials: materials.value
        })
        
        console.log('Save successful, response:', response.data)
        
        // Update local materials with the saved response to ensure we have the latest data
        if (response.data.course_materials) {
          materials.value = [...response.data.course_materials]
        }
        
        toast.success('Course materials updated successfully!')
        isDirty.value = false
        emit('updated')
      } catch (error) {
        console.error('Save error:', error)
        console.error('Error response:', error.response?.data)
        const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to save course materials'
        toast.error(errorMsg)
      } finally {
        saving.value = false
      }
    }

    return {
      materials,
      isDragging,
      uploading,
      uploadProgress,
      uploadingFile,
      saving,
      fileInput,
      isDirty,
      markDirty,
      handleFileSelect,
      handleDrop,
      getFileIcon,
      moveMaterial,
      deleteMaterial,
      saveMaterials
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content.materials-manager {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--color-text, #111);
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #666;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.video-info {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.video-info h3 {
  margin: 0 0 8px 0;
  color: var(--color-text, #111);
}

.text-muted {
  color: #6b7280;
  margin: 0;
}

.upload-section {
  margin-bottom: 32px;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  transition: all 0.2s;
  background: #f9fafb;
}

.upload-area.drag-over {
  border-color: var(--color-primary, #00a79d);
  background: rgba(0, 167, 157, 0.05);
}

.upload-icon {
  font-size: 48px;
  color: var(--color-primary, #00a79d);
  margin-bottom: 16px;
}

.upload-area h4 {
  margin: 0 0 8px 0;
  color: var(--color-text, #111);
}

.upload-area p {
  margin: 8px 0;
  color: #6b7280;
}

.upload-hint {
  font-size: 13px !important;
  margin-top: 16px !important;
}

.upload-progress {
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: var(--color-primary, #00a79d);
  transition: width 0.3s;
}

.materials-list h4 {
  margin: 0 0 16px 0;
  color: var(--color-text, #111);
}

.materials-grid {
  display: grid;
  gap: 16px;
}

.material-card {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  align-items: center;
}

.material-thumbnail {
  width: 80px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.material-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  font-size: 32px;
  color: #d1d5db;
}

.material-info {
  flex: 1;
}

.material-title-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
}

.material-description-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 6px;
  font-family: inherit;
  resize: vertical;
  min-height: 48px;
}

.material-description-input::placeholder {
  color: #9ca3af;
  font-style: italic;
}

.material-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6b7280;
}

.file-type {
  background: var(--color-primary, #00a79d);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.material-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover:not(:disabled) {
  background: var(--color-primary, #00a79d);
  color: white;
  border-color: var(--color-primary, #00a79d);
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-icon.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
  color: white;
}

.empty-materials {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
}

.empty-materials i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.auto-save-note {
  color: #059669;
  font-size: 14px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.changes-note {
  color: #f59e0b;
  font-size: 14px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-buttons {
  display: flex;
  gap: 12px;
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--color-primary, #00a79d);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary, #5ac8c0);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background: #e5e7eb;
}
</style>

